/**
 * @import * as ProductsBundleType from "@/src/types/products-bundle"
 * @import * as ShopifyType from "@/src/types/shopify"
 */

const BaseProductsBundleSectionEvent = {
  // matchProductChange: 'bundle-match-product-change',
  variantChange: 'bundle-variant-change',
}

// MainBundle数据模型，不可直接更改状态，必须通过内建函数间接更改状态；
class MainBundleNotifier extends CustomUtils.ChangeNotifier {
  /** 当前选择的产品变体 
   * @type {ProductsBundleType.BundleSetItem[]}
   */
  _currentSet = [];
  /** 搭配索引，array 的索引为第一维，值为第二维 
   * @type {number[]}
   */
  _matchIndexes = [];
  /** 搭配组合对应的插件产品 
   * @type {ProductsBundleType.BundleProduct}
   */
  _bundleProduct = undefined;
  /** 搭配组合对应的插件产品的具体变体，一般只用于 SimpleBundle 
   * @type {ShopifyType.ShopifyVariant}
   */
  _bundleProductVariant = undefined;

  constructor() {
    super();
  }

  get currentSet() { return this._currentSet; }
  get matchIndexes() { return this._matchIndexes; }
  get bundleProduct() { return this._bundleProduct; }
  get bundleProductVariant() { return this._bundleProductVariant; }

  setCurrentSet(currentSet) {
    this._currentSet = currentSet;
    this.notifyListeners(['currentSet']);
  }

  setCurrentSetWithAppBundle(currentSet, bundleProduct, bundleProductVariant) {
    this._currentSet = currentSet;
    this._bundleProduct = bundleProduct;
    this._bundleProductVariant = bundleProductVariant;
    this.notifyListeners(['currentSet', 'bundleProduct', 'bundleProductVariant']);
  }

  setMatchIndexes(matchIndexes) {
    this._matchIndexes = matchIndexes;
    this.notifyListeners(['matchIndexes']);
  }
}

// SubProduct卡片的数据模型，不可直接更改状态，必须通过内建函数间接更改状态；
class BundleSubProductInfoNotifier extends CustomUtils.ChangeNotifier {
  /** 当前选择的产品变体 
   * @type {ShopifyType.ShopifyVariant}
    */
  _variant = undefined;
  /** 当前选择的产品变体
   * @type {BundleSetItem[]}
   */
  _currentSet = undefined;
  /** 当前对应的组合产品，仅在 matching_type 为 infinite_options 和 simple_bundle 下有效
   * @type {ProductsBundleType.BundleProduct}
   */
  _bundleProduct = undefined;
  /** 当前对应的组合产品的具体变体，仅在 matching_type 为 infinite_options 和 simple_bundle 下有效
   * @type {ShopifyType.ShopifyVariant}
   */
  _bundleProductVariant = undefined;

  constructor() {
    super();
  }

  get variant() { return this._variant; }

  setVariant(variant) {
    this._variant = variant;
    this.notifyListeners(['variant']);
  }
}

const mainBundleNotifier = new MainBundleNotifier();

/** Bundle 产品相关的工具函数 */
const bundleUtils = {
  /** 获取 Bundle 产品的附加选项，转换格式
   * @param {ProductsBundleType.BundleProduct} bundleProduct
   * @return {ProductsBundleType.FormatedOption[]}
   */
  getBundleProductAdditionalOptions(bundleProduct) {
    return bundleProduct.add_bundle_options.map(addOption => {
      const split1 = addOption.includes('：') ? addOption.split('：') : addOption.split(':');
      if (split1.length < 2) console.error('Split add_bundle_options with ":" not found.', addOption);
      const split2 = split1[0].includes('—') ? split1[0].split('—') : split1[0].split('-');
      if (split2.length < 2) console.error('Split add_bundle_options with "—" not found.', addOption);
      return {
        title: split2[0]?.trim(),
        optionName: split2[1]?.trim(),
        optionValue: split1[1],
      };
    });
  },
  /** 合并 SImpleBundle 产品的所有 Options，并转换格式
   * @param {ProductsBundleType.BundleProduct} bundleProduct
   * @return {ProductsBundleType.FormatedOption[]}
   */
  mergeSimpleBundleProductOptions(bundleProduct) {
    const bundleAddOptions = this.getBundleProductAdditionalOptions(bundleProduct);
    /** @type {{title: string, optionName: string, optionValue: string}[]} */
    const fullOptions = [
      ...bundleProduct.info.variants.reduce((prev, bundleVariant, index) => {
        const options = bundleProduct.info.options.map((option, optionIndex) => {
          const split = option.split('—');
          return {
            title: split[0]?.trim(),
            optionName: split[1]?.trim(),
            optionValue: bundleVariant.options[optionIndex],
          }
        });
        return [...prev, ...options];
      }, []),
      ...bundleAddOptions,
    ];
    return fullOptions;
  },
  /** 合并 SImpleBundle 变体的 Options，并转换格式
   * @param {ProductsBundleType.BundleProduct} bundleProduct 
   * @param {ShopifyType.ShopifyVariant} bundleProductVariant 
   * @return {ProductsBundleType.FormatedOption[]}
   */
  mergeSimpleBundleVariantOptions(bundleProduct, bundleProductVariant) {
    const bundleAddOptions = this.getBundleProductAdditionalOptions(bundleProduct);
    const mergeOptions = [
      ...bundleProduct.info.options.map((option, optionIndex) => {
        const split = option.split('—');
        return {
          title: split[0]?.trim(),
          optionName: split[1]?.trim(),
          optionValue: bundleProductVariant.options[optionIndex],
        }
      }),
      ...bundleAddOptions,
    ]
    return mergeOptions;
  },
  /**
   * 将Bundle的变体，转换成对应的子产品变体列表
   * @param {ProductsBundleType.MatchingType} type 
   * @param {ProductsBundleType.SubProductCeil[]} currentSubProducts 当前搭配的子产品列表
   * @param {ProductsBundleType.BundleProduct} bundleProduct 
   * @param {ShopifyType.ShopifyVariant} bundleProductVariant 
   * @returns {ProductsBundleType.BundleSetItem[]}
   */
  bundleVariantToSubProductVariants(type, currentSubProducts, bundleProduct, bundleProductVariant) {
    if (type == 'simple_bundle') {
      const bundleOptions = this.mergeSimpleBundleVariantOptions(bundleProduct, bundleProductVariant);
      const currentSet = currentSubProducts.map((product) =>
        product.variants.find((variant) =>
          variant.options.every((optionValue, optionIndex) =>
            bundleOptions.find((bundleOption) =>
              (bundleOption.title == product.title) && (bundleOption.optionName == product.options[optionIndex]) && (bundleOption.optionValue == optionValue))))
        ?? product.variants[0]
      );
      return currentSet;
    } else if (type == 'infinite_options') {
      /** @todo */
    }
  },
  /**
   * 子产品变体列表，转换成对应的 Bundle 的变体
   * @param {ProductsBundleType.MatchingType} type 
   * @param {ProductsBundleType.BundleSetItem[]} subProductVariants 
   * @returns {ShopifyType.ShopifyVariant}
   */
  // subProductVariantsToBundleVariant(type, subProductVariants) {

  // }
}

class ProductsBundleSubProductInfo extends HTMLElement {
  /** @type {ProductsBundleType.SubProductInfo} */
  data = undefined;
  selectOptons = [];

  get product() { return this.data.product };
  get row() { return this.data.row };
  get column() { return this.data.column };
  get isGift() { return this.data.detail.data };
  get variant() {
    return this.infoNotifier.variant;
  };

  constructor() {
    super();

    /** @type {BaseProductsBundleSection} */
    this.section = this.closest('[data-section-main-products-bundle]');

    this._handleFormChange = this.handleFormChange.bind(this);
    this._handleClick = this.handleClick.bind(this);

    this.infoNotifier = new BundleSubProductInfoNotifier();
    this.provider = new CustomUtils.ChangeNotifierProvider(this.infoNotifier, this.render.bind(this));

    this._onMainNotifierChange = this.onMainNotifierChange.bind(this);
    this.mainNotifier = mainBundleNotifier;
  }

  connectedCallback() {
    this.elements = {
      price: this.querySelector('[data-sub-product-info-price]'),
      comparePrice: this.querySelector('[data-sub-product-info-compare-price]'),
      form: this.querySelector('form'),
      /** @type {HTMLElement[]} */
      radioOptionItemList: this.querySelectorAll('input[type="radio"]'),
      /** 选项弹窗按钮 */
      selectTrigger: this.querySelectorAll('[data-option-select-trigger]'),
    }
    /** @type {ProductsBundleType.SubProductInfo} */
    const jsonData = JSON.parse(this.querySelector('[data-sub-product-info-json]').innerHTML);
    this.data = jsonData;
    const variant = this.data.product.variants.find(v => v.available) ?? this.data.product.variants[0];
    this.infoNotifier.setVariant(variant);
    this.selectOptons = variant.options;

    this.elements.form.addEventListener('change', this._handleFormChange);
    mainBundleNotifier.addListener(this._onMainNotifierChange);
    this.addEventListener('click', this._handleClick);
    this.render();
  }

  disconnectedCallback() {
    this.elements.form.removeEventListener('change', this._handleFormChange);
    this.removeEventListener('click', this._handleClick);
    mainBundleNotifier.removeListener(this._onMainNotifierChange);
  }

  /**
   * @param {string} property
   */
  render(property) {
    const target = this.infoNotifier;
    if (target.variant == undefined) {
      console.error(`Undefined error: this.ref.variant is undefined in ${this.tagName}`);
      return;
    }

    // 区分不同功能类型下的渲染
    if (this.data.matching_type == 'simple_bundle') {
      this.renderTypeSimpleBundle(property);
    } else if (this.data.matching_type == 'infinite_options') {
      this.renderTypeInfiniteOptions(property);
    } else if (this.data.matching_type == 'products_discount') {
      this.renderDefaultProduct(property);
    }

    // 选项名和已选值展示
    const optionsLength = target.variant.options.length;
    for (let i = 0; i < optionsLength; i++) {
      const optionValue = target.variant.options[i];
      const optionName = this.data.product.options[i];
      const positionGroupElement = this.querySelector(`[data-option-position="${i + 1}"]`);
      if (!positionGroupElement) continue;
      for (const element of positionGroupElement.querySelectorAll('[data-option-name]')) {
        element.innerHTML = optionName;
      }
      for (const element of positionGroupElement.querySelectorAll('[data-option-value]')) {
        element.innerHTML = optionValue;
      }
      // 移动端已选值
      for (const element of positionGroupElement.querySelectorAll('[data-option-select-html]')) {
        const input = positionGroupElement.querySelector(`[data-radio-inputs-wrapper] input:checked`);
        if (input) element.innerHTML = input.parentElement.outerHTML;
      }
    }
  }

  /**
   * @param {string} property
   */
  renderTypeSimpleBundle(property) {
    if (this.data.is_gift) {
      // 赠品用普通的渲染方式
      return this.renderDefaultProduct(property);
    }
    const target = this.infoNotifier;
    const bundleProduct = this.mainNotifier.bundleProduct;
    const bundleProductVariant = this.mainNotifier.bundleProductVariant;

    if (bundleProduct == undefined || bundleProductVariant == undefined) {
      // 匹配不到 Bundle 时，用普通的渲染方式
      return this.renderDefaultProduct(property);
    }

    this.selectOptons = target.variant.options;

    if (bundleProduct != undefined) {
      // 渲染价格
      const rate = 1 - this.data.discount;
      const price = rate * target.variant.price;
      const comparePrice = (target.variant.compare_at_price > target.variant.price) ? target.variant.compare_at_price : target.variant.price;
      this.elements.price.innerHTML = themeVendor.themeCurrency.formatMoney(price, theme.moneyFormat);
      this.elements.comparePrice.innerHTML = (comparePrice > price) ? themeVendor.themeCurrency.formatMoney(comparePrice, theme.moneyFormat) : '';

      // 附加选项
      const bundleAddOptions = bundleUtils.getBundleProductAdditionalOptions(bundleProduct);
      /** @type {{title: string, optionName: string, optionValue: string}[]} */
      const fullOptions = [
        ...bundleProduct.info.variants.reduce((prev, bundleVariant, index) => {
          const options = bundleProduct.info.options.map((option, optionIndex) => {
            const split = option.split('—');
            return {
              title: split[0]?.trim(),
              optionName: split[1]?.trim(),
              optionValue: bundleVariant.options[optionIndex],
            }
          });
          return [...prev, ...options];
        }, []),
        ...bundleAddOptions
      ];

      // 更新选项视图
      for (let optionItemEl of this.elements.radioOptionItemList) {
        const positionElement = optionItemEl.closest("[data-option-position]");
        const optionPosition = Number(positionElement.getAttribute('data-option-position'));
        const optionName = positionElement.getAttribute('data-select-label');
        const value = optionItemEl.value;
        const nowIsActived = optionItemEl.checked;
        const needIsActived = value == this.selectOptons[optionPosition - 1];
        if (nowIsActived != needIsActived) {
          // 更新选项激活状态
          optionItemEl.checked = !nowIsActived;
        }

        // 隐藏不存在搭配的变体选项
        const findOption = fullOptions.find(option => option.title == this.product.title && option.optionName == optionName && option.optionValue == value);
        const tempOptions = [...this.selectOptons];
        tempOptions[optionPosition - 1] = value;
        const isExist = this.data.product.variants.findIndex((v) => this.equalOptions(v.options, tempOptions)) > -1;
        if (findOption && isExist) {
          optionItemEl.parentElement.removeAttribute('hidden');
        } else {
          optionItemEl.parentElement.setAttribute('hidden', '');
        }

        /**
         * @type {{title:string, optionName:string, optionValue:string}[]}
         * 当前搭配Options，转换格式
         */
        let matchesOptions = this.section.matchesOptions.map((v) => ({ ...v }));
        const findIndex = matchesOptions.findIndex((option) => option.title == this.product.title && option.optionName == optionName);
        // 该变体选项对应的搭配Options
        if (findIndex > -1) matchesOptions[findIndex].optionValue = value;

        // 根据新搭配匹配对应的 Bundle 变体
        const findBundleVariant = bundleProduct.info.variants.find((bundleVariant, index) => {
          const options = bundleProduct.info.options.map((option, optionIndex) => {
            const split = option.split('—');
            return {
              title: split[0]?.trim(),
              optionName: split[1]?.trim(),
              optionValue: bundleVariant.options[optionIndex],
            }
          });
          const fullOptions = [...options, ...bundleAddOptions];
          // 判断子产品是否都在当前搭配中
          return fullOptions.every(item => {
            const find = matchesOptions.find((matchOption) => matchOption.title == item.title && matchOption.optionName == item.optionName && matchOption.optionValue == item.optionValue)
            return !!find;
          });
        });
        // 库存状态
        findBundleVariant?.available
          ? optionItemEl.classList.remove('unavailable')
          : optionItemEl.classList.add('unavailable');
      }
    }
  }

  /**
   * @param {string} property
   */
  renderTypeInfiniteOptions(property) {
    // 渲染逻辑同 products_discount
    this.renderDefaultProduct(property);
  }

  /**
   * @param {string} property
   */
  renderDefaultProduct(property) {
    const target = this.infoNotifier;
    this.selectOptons = target.variant.options;

    // 价格
    const rate = (100 - this.data.discount * 100) / 100;
    // 折后价，应用了 bundle 折扣，赠品固定折后价为0
    // const price = this.data.is_gift ? 0 : (rate * target.variant.price);
    const price = this.data.is_gift ? 0 : Math.round((100 * rate * target.variant.price) / 100);
    // 原始的产品价格
    const comparePrice = (target.variant.compare_at_price > target.variant.price) ? target.variant.compare_at_price : target.variant.price;
    this.elements.price.innerHTML = themeVendor.themeCurrency.formatMoney(price, theme.moneyFormat);
    this.elements.comparePrice.innerHTML = (comparePrice > price) ? themeVendor.themeCurrency.formatMoney(comparePrice, theme.moneyFormat) : '';

    // 更新选项视图
    for (let optionItemEl of this.elements.radioOptionItemList) {
      const optionPosition = Number(optionItemEl.closest("[data-option-position]").getAttribute('data-option-position'));
      const value = optionItemEl.value;
      const nowIsActived = optionItemEl.checked;
      const needIsActived = value == this.selectOptons[optionPosition - 1];
      if (nowIsActived != needIsActived) {
        // 更新选项激活状态
        optionItemEl.checked = !nowIsActived;
      }

      const tempOptions = [...this.selectOptons];
      tempOptions[optionPosition - 1] = value;

      // 更新库存状态
      const findVariant = this.data.product.variants.find((v) => this.equalOptions(v.options, tempOptions));
      if (findVariant?.available) {
        optionItemEl.classList.remove('sold-out');
      } else {
        optionItemEl.classList.add('sold-out');
      }

      // 有时，该选项不能与其他已选项搭配在一起，因为可能没有这样搭配的变体
      const isExist = this.data.product.variants.findIndex((v) => this.equalOptions(v.options, tempOptions)) > -1;
      const isDisabled = optionItemEl.classList.contains('unavailable');
      if (isExist == isDisabled) {
        isExist
          ? optionItemEl.classList.remove('unavailable')
          : optionItemEl.classList.add('unavailable');
        isExist
          ? optionItemEl.parentElement.classList.remove('unavailable')
          : optionItemEl.parentElement.classList.add('unavailable');
      }
    }
  }

  /**
   * @param {string[]} property 
   */
  onMainNotifierChange(property) {
    if (property.includes('currentSet')
      && mainBundleNotifier.matchIndexes[this.row] == this.column
    ) {
      const variant = mainBundleNotifier.currentSet[this.row];
      if (variant) {
        this.infoNotifier.setVariant(variant);
      }
    }
  }

  handleFormChange(e) {
    /** @type {HTMLElement} */
    const target = e.target;
    if (target.matches('input[type="radio"]')) {
      const optionPosition = Number(target.closest('[data-option-position]').getAttribute('data-option-position'));

      /** @todo 可能需要注意选项名被 escape 过滤过 */
      // const optionName = target.getAttribute('name').replace(/options\[(.*?)\]/, '$1');
      const optionValue = target.value;

      const options = [...this.infoNotifier.variant.options];
      options[optionPosition - 1] = optionValue;

      let findVarinat = this.data.product.variants.find((v) => this.equalOptions(v.options, options));
      if (findVarinat) {
        this.infoNotifier.setVariant(findVarinat);
      } else {
        console.warn(`Variant not found!`)
        findVarinat = this.data.product.variants.find((v) => v.options[optionPosition - 1] == optionValue);
        if (findVarinat) {
          this.infoNotifier.setVariant(findVarinat)
        }
      }

      this.dispatchEvent(new CustomEvent(BaseProductsBundleSectionEvent.variantChange, {
        bubbles: true,
        detail: {
          data: this.data,
          variant: findVarinat,
        },
      }));
      // 关闭弹窗
      this.querySelector('[data-radio-inputs-wrapper].active')?.classList.remove('active');
    }
  }

  handleClick(e) {
    /** @type {HTMLElement} */
    const target = e.target;
    const seleteTrigger = target.closest('[data-option-select-trigger]');
    if (seleteTrigger) {
      seleteTrigger.nextElementSibling.classList.add('active');
    }
    if (target.matches('[data-radio-inputs-overlay]')) {
      target.previousElementSibling.classList.remove('active');
    }
  }

  equalOptions(options1, options2) {
    return options1.toString() == options2.toString();
  }
}


if (!window.customElements.get('main-products-bundle-sub-product-info')) {
  window.customElements.define('main-products-bundle-sub-product-info', ProductsBundleSubProductInfo);
}

class BaseProductsBundleSection extends HTMLElement {
  /**
   * @type {ProductsBundleType.ProductsBundleData}
   */
  data = undefined;
  /**
   * @type {Swiper[]}
   */
  swiperList = [];
  /** 
   * 子产品，二维数组，对应滑块的横纵
   * @type {ProductsBundleType.SubProductRow[]}
   */
  subProducts = [];

  /**
   * 获取当前搭配的产品列表
   */
  get currentSubProducts() {
    return this.subProducts.map((row, i) => row.columns[this.notifier.matchIndexes[i]]).filter(product => product !== undefined);
  }

  /**
   * 获取当前搭配的产品列表，不含赠品
   */
  get currentSubProductsNoGift() {
    return this.subProducts.map((row, i) => row.columns[this.notifier.matchIndexes[i]]).filter(product => product !== undefined && !product.is_gift);
  }

  /**
   * 获取当前搭配的各个产品变体，且不包含赠品
   */
  get currentSetNoGift() {
    return this.notifier.currentSet.filter((item, index) => !this.subProducts[index].is_gift);
  }

  /**
   * 获取当前搭配的各个产品变体，且不包含赠品
   */
  get matchesOptions() {
    const matchesOptions = [];
    for (let productIndex = 0; productIndex < this.currentSubProductsNoGift.length; productIndex++) {
      const subProduct = this.currentSubProductsNoGift[productIndex];
      const options = subProduct.options.map((option, optionIndex) => ({
        title: subProduct.title.trim(),
        optionName: option.trim(),
        optionValue: this.currentSetNoGift[productIndex].options[optionIndex].trim(),
      }));
      matchesOptions.push(...options)
    }
    return matchesOptions;
  }

  constructor() {
    super();
    this.data = JSON.parse(document.querySelector('[data-main-products-bundle-json]').innerHTML);

    this._handleAddToCart = this.handleAddToCart.bind(this);
    this.notifier = mainBundleNotifier;
    this._onWindowScroll = this.onWindowScroll.bind(this);
  }

  connectedCallback() {
    this.elements = {
      drawer: document.querySelector("[data-drawer='drawer-cart']"),
      matchingRows: this.querySelectorAll('[data-matching-row]'),
      price: this.querySelector('[data-total-price]'),
      comparePrice: this.querySelector('[data-total-compare-price]'),
      priceOff: this.querySelector('[data-price-off]'),
      priceOffAmount: this.querySelector('[data-price-off-amount]'),
      buttonPrice: this.querySelector('[data-button-price]'),
      buttonComparePrice: this.querySelector('[data-button-compare-price]'),
      /** @type {ProductsBundleSubProductInfo[]} */
      subProductInfoList: [...this.querySelectorAll('[data-sub-product-info]')],
      addCartButton: this.querySelector('[data-add-to-cart-button]'),
      addCartButtonText: this.querySelector('[data-add-to-cart-button] [data-add-to-cart-text]'),
      klarna: this.querySelector('klarna-placement'),
      paypal: this.querySelector('[data-pp-amount]'),
      reviews: this.querySelector('[data-gw-product-id]'),
    }
    this.addEventListener(BaseProductsBundleSectionEvent.variantChange, this.onVariantChange);
    this.elements.addCartButton.addEventListener('click', this._handleAddToCart);
    window.addEventListener("scroll", this._onWindowScroll);

    this.init();
    this.provider = new CustomUtils.ChangeNotifierProvider(mainBundleNotifier, this.render.bind(this));
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener(BaseProductsBundleSectionEvent.variantChange, this.onVariantChange);
    this.elements.addCartButton.removeEventListener('click', this._handleAddToCart);
    window.removeEventListener("scroll", this._onWindowScroll);
    this.provider.dispose();
  }

  init() {
    // 初始化子产品信息
    this.subProducts = [];
    for (const item of this.elements.subProductInfoList) {
      /** @type {ProductsBundleType.SubProductInfo} */
      const info = JSON.parse(item.querySelector('[data-sub-product-info-json]').innerHTML);
      if (this.subProducts[info.row] == undefined) {
        this.subProducts[info.row] = {
          is_gift: info.is_gift,
          columns: [],
        };
      }
      this.subProducts[info.row].columns[info.column] = { ...info.product, is_gift: info.is_gift };
    }
    // 初始化轮播图
    const matchingRowsLength = this.elements.matchingRows.length;
    for (let i = 0; i < matchingRowsLength; i++) {
      const row = this.elements.matchingRows[i];
      this.swiperList[i] = new Swiper(row, {
        slidesPerView: 'auto',
        centeredSlides: true,
        slideToClickedSlide: true,
        observer: true,
        resizeObserver: true,
        navigation: {
          prevEl: row.querySelector('[data-swiper-button-prev]'),
          nextEl: row.querySelector('[data-swiper-button-next]'),
        },
        on: {
          slideChange: (swiper) => this.handleSlideChange(i, swiper),
        }
      });
    }
    // 初始化当前搭配
    const matchIndexes = Array.from({ length: matchingRowsLength }, () => 0);
    this.notifier.setMatchIndexes(matchIndexes);

    // 覆写
  }

  /**
   * @param {string[]} property
   */
  render(property) {
    const target = mainBundleNotifier;
    // 切换展示选项卡
    for (let _rowIndex = 0; _rowIndex < target.matchIndexes.length; _rowIndex++) {
      const columnIndex = target.matchIndexes[_rowIndex];
      this.elements.subProductInfoList.find((item) => item.matches(`[data-sub-product-row="${_rowIndex}"].active`))?.classList.remove('active');
      const findInfoElement = this.elements.subProductInfoList.find((item) => item.matches(`[data-sub-product-row="${_rowIndex}"][data-sub-product-column="${columnIndex}"]`));
      if (findInfoElement) {
        findInfoElement.classList.add('active');
      } else {
        console.error('Variant not found!', _rowIndex, columnIndex)
      }
    }

    // 覆写
  }

  /**
   * 滑块切换图片
   * @param {number} row 
   * @param {number} column 
   * @param {ShopifyType.ShopifyVariant} variant 
   */
  renderSwiperSlide(row, column, variant) {
    const slide = this.swiperList[row].slides[column];
    if (variant.featured_image?.src) {
      slide.querySelector('[data-product-image]').src = variant.featured_image.src;
    }
  }

  onWindowScroll() {
    const wrapperWrapper = this.elements.addCartButton.parentElement.parentElement;
    const wrapper = this.elements.addCartButton.parentElement;
    if (window.innerWidth < 768) {
      const offsetTop = wrapperWrapper.offsetTop + 50;
      if (document.scrollingElement.scrollTop > offsetTop) {
        wrapper.classList.add("fixed");
      } else {
        wrapper.classList.remove("fixed");
      }
    } else if (wrapper.classList.contains("fixed")) {
      wrapper.classList.remove("fixed");
    }
  }

  /** 
   * 当前搭配下的子产品切换了变体
   * @param {ProductsBundleType.VariantChangeEvent} e 
   */
  onVariantChange(e) {
    const detail = e.detail;

    if (!(detail.data.row != undefined && detail.data.column != undefined && detail.variant != undefined)) {
      console.error(`onVariantChange: detail has undefined value! `, detail);
      return;
    }
    this.renderSwiperSlide(detail.data.row, detail.data.column, detail.variant);

    // 覆写
  }

  /**
   * @param {number} rowIndex
   * @param {Swiper} swiper
   */
  handleSlideChange(rowIndex, swiper) {
    const columnIndex = swiper.activeIndex;

    const matchIndexes = [...mainBundleNotifier.matchIndexes];
    matchIndexes[rowIndex] = columnIndex;
    mainBundleNotifier.setMatchIndexes(matchIndexes);

    // 覆写
  }

  /**
   * @abstract
   * 获取加购的商品列表
   * @returns {[{id: string, quantity: number, properties: object}[]]}
   */
  getAddToCartItems() { };

  /**
   * 加购按钮
   */
  handleAddToCart(e) {
    this.elements.addCartButton.classList.add("loading");
    const items = this.getAddToCartItems();

    if (items.length == 0) {
      this.elements.addCartButton.classList.remove("loading");
      return console.error('Error empty items to add to cart!');
    }
    this.addToCart(items).finally(() => this.elements.addCartButton.classList.remove("loading"));
  }

  /**
   * 加购请求
   * @param {[{id: string, quantity: number}]} items 
   */
  async addToCart(items) {
    let formData = {
      'items': items
    };
    return fetch(window.Shopify.routes.root + "cart/add.js",
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData),
      }
    ).then((response) => {
      if (response.ok) {
        if (this.elements.drawer) {
          this.elements.drawer.dispatchEvent(
            new CustomEvent('theme:drawer:toggle', {
              bubbles: false,
            })
          );
        }
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        return response.json();
      } else if (response.status == 422) {
        // 售罄
        alert('Sold out');
      }
    }).catch((err) => {
      console.error(err);
    });
  }
}

class SimpleBundleSection extends BaseProductsBundleSection {
  constructor() {
    super();

    this.data.matching_type = 'simple_bundle';
  }

  /**
   * @override
   */
  init() {
    super.init();

    // 初次加载时，尽量选择有库存的组合，就算都没库存至少也需要切换到能组合成 bundle 的选项 
    const bundleProduct = this.computedSimpleBundleProduct;
    if (bundleProduct != undefined) {
      const findAvailableBundleVariant = bundleProduct?.info.variants.find((v) => v.available) ?? bundleProduct?.info.variants[0];
      const set = bundleUtils.bundleVariantToSubProductVariants(this.data.matching_type, this.currentSubProducts, bundleProduct, findAvailableBundleVariant);
      this.notifier.setCurrentSetWithAppBundle(set, bundleProduct, findAvailableBundleVariant)
    } else {
      const set = this.currentSubProducts.map((product) => ({ ...product.variants.find((v) => v.available) ?? product.variants[0], is_gift: product.is_gift }));
      this.notifier.setCurrentSetWithAppBundle(set, undefined, undefined)
    }
  }

  /**
   * @override
   * @param {string[]} property
   */
  render(property) {
    super.render();

    const target = mainBundleNotifier;
    const rate = 1 - this.data.discount ?? 0;
    if ((!property || property == 'bundleProduct' || (property == 'currentSet'))) {
      // 组合套件价格计算，需排除赠品的价格
      const rate = 1 - (this.data.discount ?? 0);
      // 普通划线价
      const totalComparePrice = target.bundleProductVariant?.compare_at_price ?? target.currentSet.reduce((prev, curr) => prev + (curr.is_gift ? 0 : curr.price), 0);
      // 实际价格
      const totalPrice = target.bundleProductVariant?.price ?? (rate * totalComparePrice);
      this.elements.price.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      this.elements.buttonPrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      if (this.data.discount || totalPrice < totalComparePrice) {
        this.elements.comparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalComparePrice, theme.moneyFormat);
        this.elements.buttonComparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalComparePrice, theme.moneyFormat);
        this.elements.priceOff.classList.remove('hidden');
        this.elements.priceOffAmount.innerHTML = this.data.discount
          ? ((this.data.discount * 100) + '%')
          : Math.round((totalComparePrice - totalPrice) / totalComparePrice * 100) + '%';
      } else {
        this.elements.comparePrice.innerHTML = '';
        this.elements.buttonComparePrice.innerHTML = '';
        this.elements.priceOff.classList.add('hidden');
        this.elements.priceOffAmount.innerHTML = '';
      }
      // klarna
      this.elements.klarna?.setAttribute('data-purchase-amount', Math.round(totalPrice));
      this.elements.paypal?.setAttribute('data-pp-amount', Math.round(totalPrice) / 100);
    }

    // 加购按钮状态 
    if (target.bundleProductVariant == undefined) {
      this.elements.addCartButton.classList.add('variant--soldout');
      this.elements.addCartButton.setAttribute('disabled', '');
      // 匹配不到 Bundle 产品时，显示不可用。
      this.elements.addCartButtonText.innerHTML = window.theme.strings.unavailable;
    } else if (target.bundleProductVariant.available) {
      this.elements.addCartButton.classList.remove('variant--soldout');
      this.elements.addCartButton.removeAttribute('disabled');
      this.elements.addCartButtonText.innerHTML = window.theme.strings.addToCart;
    } else {
      this.elements.addCartButton.classList.add('variant--soldout');
      this.elements.addCartButton.setAttribute('disabled', '');
      this.elements.addCartButtonText.innerHTML = window.theme.strings.soldOut;
    }

    // Reviews 组件更新
    if (this.elements.reviews && (this.elements.reviews?.getAttribute('data-product-id') != target.bundleProduct?.id)) {
      this.elements.reviews?.setAttribute('data-gw-product-id', target.bundleProduct?.id);
      this.elements.reviews?.setAttribute('data-product-id', target.bundleProduct?.id);
      this.elements.reviews.outerHTML = this.elements.reviews.outerHTML;
    }
  }

  /** 
   * @override
   * 当前搭配下的子产品切换了变体
   * @param {ProductsBundleType.VariantChangeEvent} e 
   */
  onVariantChange(e) {
    super.onVariantChange(e);
    const detail = e.detail;

    const currentSet = this.notifier.currentSet;
    currentSet[detail.data.row] = { ...detail.variant, is_gift: detail.data.is_gift };
    this.notifier.setCurrentSetWithAppBundle(currentSet, this.computedSimpleBundleProduct, this.computedSimpleBundleProductVariant);
  }

  /** 
   * @override
   * @param {number} rowIndex
   * @param {Swiper} swiper
   */
  handleSlideChange(rowIndex, swiper) {
    super.handleSlideChange(rowIndex, swiper);

    const columnIndex = swiper.activeIndex;
    // 该行对应的子产品信息卡片
    const findInfoElement = this.elements.subProductInfoList.find((item) => item.matches(`[data-sub-product-row="${rowIndex}"][data-sub-product-column="${columnIndex}"]`));
    if (findInfoElement) {
      // 同步更新 currentSet
      const currentSet = this.notifier.currentSet;
      currentSet[rowIndex] = { ...findInfoElement.variant, is_gift: findInfoElement.data.is_gift };
      this.notifier.setCurrentSetWithAppBundle(currentSet, this.computedSimpleBundleProduct, this.computedSimpleBundleProductVariant);
    }
  }

  /**
   * @override
   */
  getAddToCartItems() {
    const findBundleVariant = this.computedSimpleBundleProductVariant;
    if (!!findBundleVariant) {
      return [
        {
          id: findBundleVariant.id,
          quantity: 1,
        },
        // Gift
        ...this.currentSubProducts
          .reduce((acc, item, index) => item.is_gift ? [...acc, index] : acc, [])
          .map(i => ({
            id: this.notifier.currentSet[i].id,
            quantity: 1,
            properties: {
              "_bundle_gift_for_variant": findBundleVariant.id,
            }
          }))
      ];
    } else {
      console.error('getAddToCartItems: findBundleVariant not found!', this.notifier.bundleProduct)
      return [];
    }
  }

  /** 获取当前对应的插件组合产品（Simple bundle）
   * 需要注意缺失的 Option 在 [add_bundle_options] 中，也要并入判断
   * @returns {ProductsBundleType.BundleProduct | undefined}
   */
  get computedSimpleBundleProduct() {
    // 获取当前选中的产品变体集合
    const bundleProducts = this.data.bundle_products;

    // 如果没有数据，直接返回 null
    if (!bundleProducts) {
      return null;
    }

    const currentSubProductTitles = this.currentSubProducts.map(product => product.title);

    // 遍历所有 bundle products
    const findBundle = bundleProducts.find(bundle => {
      const optionList = [
        ...bundle.info.options,
        ...bundle.add_bundle_options, // 补充的 Option 虽然带有冒号和OptionValue，但可以无视
      ]
      // bundle 的每个 option 都必须拥有当前组合搭配的产品的标题
      // 逻辑：判断 optionList 是否：每个项都能在 currentSubProductTitles 找到（只要 includes 就算）
      return optionList.every(option => currentSubProductTitles.some(title => option.includes(title)));
    });

    return findBundle;
  }

  /** 获取当前对应的插件组合产品的对应变体（Simple bundle）
   * 需要注意缺失的 Option 在 [add_bundle_options] 中，也要并入判断
   * @returns {ShopifyType.ShopifyVariant | undefined}
   */
  get computedSimpleBundleProductVariant() {
    if (!this.notifier.bundleProduct) return undefined;
    // 根据当前搭配和子产品变体选择，找到对应的 bundle 变体
    /**
     * @type {{title:string, optionName:string, optionValue:string}[]}
     * 当前搭配Options，转换格式
     */
    const matchesOptions = this.matchesOptions;

    // Bundle 附加的 Options
    const addOptions = bundleUtils.getBundleProductAdditionalOptions(this.notifier.bundleProduct);
    // 根据当前搭配匹配对应的 Bundle 变体
    const findBundleVariant = this.notifier.bundleProduct.info.variants.find((bundleVariant, index) => {
      const options = this.notifier.bundleProduct.info.options.map((option, optionIndex) => {
        const split = option.split('—');
        return {
          title: split[0]?.trim(),
          optionName: split[1]?.trim(),
          optionValue: bundleVariant.options[optionIndex],
        }
      });
      const fullOptions = [...options, ...addOptions];
      // 判断子产品是否都在当前搭配中
      return fullOptions.every(item => {
        const find = matchesOptions.find((matchOption) => matchOption.title == item.title && matchOption.optionName == item.optionName && matchOption.optionValue == item.optionValue)
        return !!find;
      });
    });
    return findBundleVariant;
  }
}

class InfiniteBundleSection extends BaseProductsBundleSection {
  constructor() {
    super();

    this.data.matching_type = 'infinite_options';
  }

  /**
   * @override
   */
  init() {
    super.init();

    // 初次加载时，尽量选择有库存的组合，就算都没库存至少也需要切换到能组合成 bundle 的选项 
    // 直接用子产品有库存的变体
    const bundleProduct = this.computedInfiniteBundleProduct;
    const set = this.currentSubProducts.map((product) => ({ ...product.variants.find((v) => v.available) ?? product.variants[0], is_gift: product.is_gift }));
    this.notifier.setCurrentSetWithAppBundle(set, bundleProduct, bundleProduct?.info.variants[0]);
  }


  /** 
   * @override
   * @param {string[]} property 
   */
  render(property) {
    super.render();

    const target = mainBundleNotifier;
    const bundle = target.bundleProduct;
    const rate = (bundle?.discount != undefined) ? (1 - bundle.discount) : 1;
    if ((!property || property == 'bundleProduct' || (property == 'currentSet' && bundle?.discount == undefined))) {
      // 组合套件价格计算，需排除赠品的价格
      const rate = 1 - (this.data.discount ?? 0);
      const totalMaxComparePrice = target.currentSet.reduce((prev, curr) =>
        prev + (curr.is_gift ? 0 : (curr.compare_at_price > curr.price ? curr.compare_at_price : curr.price))
        , 0);
      const totalPrice = rate * target.currentSet.reduce((prev, curr) => prev + (curr.is_gift ? 0 : curr.price), 0);
      this.elements.price.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      this.elements.buttonPrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      if (totalPrice < totalMaxComparePrice) {
        this.elements.comparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalMaxComparePrice, theme.moneyFormat);
        this.elements.buttonComparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalMaxComparePrice, theme.moneyFormat);
        this.elements.priceOff.classList.remove('hidden');
        this.elements.priceOffAmount.innerHTML = Math.round((totalMaxComparePrice - totalPrice) / totalMaxComparePrice * 100) + '%';
      } else {
        this.elements.comparePrice.innerHTML = '';
        this.elements.buttonComparePrice.innerHTML = '';
        this.elements.priceOff.classList.add('hidden');
        this.elements.priceOffAmount.innerHTML = '';
      }
      // klarna
      this.elements.klarna?.setAttribute('data-purchase-amount', Math.round(totalPrice));
      this.elements.paypal?.setAttribute('data-pp-amount', Math.round(totalPrice) / 100);
    }

    // 加购按钮状态
    const variantsAvailable = this.notifier.currentSet.every(v => v.available);
    const hasBundle = !!target.bundleProduct;
    if (variantsAvailable && hasBundle) {
      this.elements.addCartButton.classList.remove('variant--soldout');
      this.elements.addCartButton.removeAttribute('disabled');
      this.elements.addCartButtonText.innerHTML = window.theme.strings.addToCart;
    } else {
      this.elements.addCartButton.classList.add('variant--soldout');
      this.elements.addCartButton.setAttribute('disabled', '');
      // 匹配到 Bundle 产品时，显示售罄；匹配不到 Bundle 产品时，显示不可用。
      this.elements.addCartButtonText.innerHTML = hasBundle ? window.theme.strings.soldOut : window.theme.strings.unavailable;
    }

    // Reviews 组件更新
    if (this.elements.reviews && (this.elements.reviews?.getAttribute('data-product-id') != target.bundleProduct?.id)) {
      this.elements.reviews?.setAttribute('data-gw-product-id', target.bundleProduct?.id);
      this.elements.reviews?.setAttribute('data-product-id', target.bundleProduct?.id);
      this.elements.reviews.outerHTML = this.elements.reviews.outerHTML;
    }
  }

  /** 
   * @override
   * 当前搭配下的子产品切换了变体
   * @param {ProductsBundleType.VariantChangeEvent} e 
   */
  onVariantChange(e) {
    super.onVariantChange(e);
    const detail = e.detail;

    const currentSet = this.notifier.currentSet;
    currentSet[detail.data.row] = { ...detail.variant, is_gift: detail.data.is_gift };
    this.notifier.setCurrentSetWithAppBundle(currentSet, this.computedInfiniteBundleProduct, this.computedInfiniteBundleProduct?.info.variants[0]);
  }

  /** 
   * @override
   * @param {number} rowIndex
   * @param {Swiper} swiper
   */
  handleSlideChange(rowIndex, swiper) {
    super.handleSlideChange(rowIndex, swiper);

    const columnIndex = swiper.activeIndex;
    // 该行对应的子产品信息卡片
    const findInfoElement = this.elements.subProductInfoList.find((item) => item.matches(`[data-sub-product-row="${rowIndex}"][data-sub-product-column="${columnIndex}"]`));
    if (findInfoElement) {
      // 同步更新 currentSet
      const currentSet = this.notifier.currentSet;
      currentSet[rowIndex] = { ...findInfoElement.variant, is_gift: findInfoElement.data.is_gift };
      this.notifier.setCurrentSetWithAppBundle(currentSet, this.computedInfiniteBundleProduct, this.computedInfiniteBundleProduct?.variants?.[0] ?? undefined);
    } else {
      console.error('Variant not found!', rowIndex, columnIndex)
    }
  }

  /**
   * @override
   */
  getAddToCartItems() {
    const currentSetNoGift = this.currentSetNoGift;
    if (!this.notifier.bundleProduct) {
      this.elements.addCartButton.classList.remove("loading");
      console.error('Bundle not found!', this.notifier.bundleProduct)
      return [];
    };
    const variant = this.notifier.bundleProduct.info.variants[0];
    const names = currentSetNoGift.map(v => v.name);
    // 属性用于购物车展示
    const properties = this.currentSubProducts.filter(pro => !pro.is_gift)
      .reduce((acc, { title }, index) => ({ ...acc, [title]: this.notifier.currentSet[index].name }), {});
    const items = [
      {
        id: variant.id,
        quantity: 1,
        properties: {
          ...properties,
          // 属性用于插件识别
          "_bundle_selection": currentSetNoGift.map(v => v.name).join(' <> ')
        }
      },
      // Gift
      ...this.currentSubProducts
        .reduce((acc, item, index) => item.is_gift ? [...acc, index] : acc, [])
        .map(i => ({
          id: this.notifier.currentSet[i].id,
          quantity: 1,
          properties: {
            "_bundle_gift_for_variant": variant.id,
          }
        }))
    ];
    return items;
  }

  /** 获取当前对应的插件组合产品（Infinite options bundle）
   * @returns {ProductsBundleType.BundleProduct | undefined}
   */
  get computedInfiniteBundleProduct() {
    const subProductsNoGift = this.currentSubProductsNoGift;
    const bundleProducts = this.data.bundle_products;

    // 如果没有数据，直接返回 null
    if (!subProductsNoGift || !bundleProducts) {
      return null;
    }

    // 遍历所有 bundle products
    for (let i = 0; i < bundleProducts.length; i++) {
      const bundleProduct = bundleProducts[i];
      // 检查是否有 variant_options
      if (!bundleProduct.variant_options) {
        continue;
      }
      // 获取 variant_options 的第一个变体
      const variantKey = Object.keys(bundleProduct.variant_options)[0];
      if (!variantKey) {
        continue;
      }
      const variantOptions = bundleProduct.variant_options[variantKey];
      // 检查 variantOptions 数量是否与 subProductsNoGift 匹配
      if (variantOptions.length !== subProductsNoGift.length) {
        continue;
      }
      // 检查每个 variant option 是否都能在 subProductsNoGift 中找到对应的产品
      const isMatch = !!variantOptions.every((option) => subProductsNoGift.find((product) => product.title.trim() === option.optionName.trim()));

      // 如果所有选项都匹配，返回这个 bundle product
      if (isMatch) {
        return bundleProduct;
      }
    }
    // 如果没有找到匹配的产品，返回 null
    return null;
  }
}

class ProductDiscountBundleSection extends BaseProductsBundleSection {
  constructor() {
    super();

    this.data.matching_type = 'products_discount';
  }

  /**
   * @override
   */
  init() {
    super.init();

    // 初次加载时，尽量选择有库存的组合，就算都没库存至少也需要切换到能组合成 bundle 的选项
    // 直接用子产品有库存的变体
    const set = this.currentSubProducts.map((product) => ({ ...product.variants.find((v) => v.available) ?? product.variants[0], is_gift: product.is_gift }));
    this.notifier.setCurrentSet(set);
  }

  /**
   * @override
   * @param {string[]} property
   */
  render(property) {
    super.render();

    const target = this.notifier;
    if (!property || property == 'currentSet') {
      const rate = 1 - (this.data.discount ?? 0);
      // 价格计算，需注意赠品价格固定改为0 
      // 最大的划线价
      const totalMaxComparePrice = target.currentSet.reduce((prev, curr) =>
        prev + (curr.is_gift ? 0 : (curr.compare_at_price > curr.price ? curr.compare_at_price : curr.price))
        , 0);
      const totalPrice = rate * target.currentSet.reduce((prev, curr) => prev + (curr.is_gift ? 0 : curr.price), 0);;
      this.elements.price.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      this.elements.buttonPrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalPrice, theme.moneyFormat);
      if (totalPrice < totalMaxComparePrice) {
        this.elements.comparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalMaxComparePrice, theme.moneyFormat);
        this.elements.buttonComparePrice.innerHTML = themeVendor.themeCurrency.formatMoney(totalMaxComparePrice, theme.moneyFormat);
        this.elements.priceOff.classList.remove('hidden');
        this.elements.priceOffAmount.innerHTML = Math.round((totalMaxComparePrice - totalPrice) / totalMaxComparePrice * 100) + '%';
      } else {
        this.elements.comparePrice.innerHTML = '';
        this.elements.buttonComparePrice.innerHTML = '';
        this.elements.priceOff.classList.add('hidden');
        this.elements.priceOffAmount.innerHTML = '';
      }
      // klarna
      this.elements.klarna?.setAttribute('data-purchase-amount', Math.round(totalPrice));
      this.elements.paypal?.setAttribute('data-pp-amount', Math.round(totalPrice) / 100);
    }

    // 加购按钮状态
    const available = this.notifier.currentSet.every(v => v.available);
    if (available) {
      this.elements.addCartButton.classList.remove('variant--soldout');
      this.elements.addCartButton.removeAttribute('disabled');
      this.elements.addCartButtonText.innerHTML = window.theme.strings.addToCart;
    } else {
      this.elements.addCartButton.classList.add('variant--soldout');
      this.elements.addCartButton.setAttribute('disabled', '');
      this.elements.addCartButtonText.innerHTML = window.theme.strings.soldOut;
    }
  }

  /** 
   * @override
   * 当前搭配下的子产品切换了变体
   * @param {ProductsBundleType.VariantChangeEvent} e 
   */
  onVariantChange(e) {
    super.onVariantChange(e);
    const detail = e.detail;

    const currentSet = this.notifier.currentSet;
    currentSet[detail.data.row] = { ...detail.variant, is_gift: detail.data.is_gift };
    this.notifier.setCurrentSet(currentSet);
  }

  /** 
   * @override
   * @param {number} rowIndex
   * @param {Swiper} swiper
   */
  handleSlideChange(rowIndex, swiper) {
    super.handleSlideChange(rowIndex, swiper);

    const columnIndex = swiper.activeIndex;
    // 该行对应的子产品信息卡片
    const findInfoElement = this.elements.subProductInfoList.find((item) => item.matches(`[data-sub-product-row="${rowIndex}"][data-sub-product-column="${columnIndex}"]`));
    if (findInfoElement) {
      // 同步更新 currentSet
      const currentSet = this.notifier.currentSet;
      currentSet[rowIndex] = { ...findInfoElement.variant, is_gift: findInfoElement.data.is_gift };
      this.notifier.setCurrentSetWithAppBundle(currentSet, this.computedSimpleBundleProduct, this.computedSimpleBundleProductVariant);
    } else {
      console.error('Variant not found!', rowIndex, columnIndex)
    }
  }

  /**
   * @override
   */
  getAddToCartItems() {
    return this.notifier.currentSet.map((v) => (
      {
        id: v.id,
        quantity: 1,
      }
    ));
  }

}

if (!window.customElements.get('main-products-simple-bundle')) {
  window.customElements.define('main-products-simple-bundle', SimpleBundleSection);
}

if (!window.customElements.get('main-products-infinite-bundle')) {
  window.customElements.define('main-products-infinite-bundle', InfiniteBundleSection);
}

if (!window.customElements.get('main-products-product-discount-bundle')) {
  window.customElements.define('main-products-product-discount-bundle', ProductDiscountBundleSection);
}

// Growave Reviews 插件样式和文本调整
const ratingWidgetWrapper = document.querySelector('.gw-rv-average-placeholder');
if (ratingWidgetWrapper) {
  // 文本调整
  const ratingWidgetObserver = new MutationObserver(function (mutationsList, observer) {
    for (let mutation of mutationsList) {
      if (mutation.type === 'childList' || mutation.type === 'attributes') {
        const target = mutation?.target;
        if (target?.classList?.contains('gw-rv-average__rating_text') && target.innerText.includes('Reviews')) {
          target.innerText = `(${target.innerText.replace('Reviews', '').trim()})`;
          ratingWidgetObserver.disconnect();
        }
      }
    }
  });

  // 样式调整
  const ratingWidgetInterval = setInterval(() => {
    if (ratingWidgetWrapper?.shadowRoot) {
      ratingWidgetWrapper.shadowRoot.appendChild(document.createElement('style')).innerText = `
      .gw-rv-average .gw-rv-average__rating .gw-rv-tooltip__tooltip {
        right: 0;
        left: initial;
      }
      .gw-rv-average .gw-rv-average__rating .gw-rv-tooltip-arrow {
        right: 5%;
        left: initial;
      }
      .gw-rv-average .gw-rv-average__rating .gw-rv-avg-rating {
        gap: 0px;
        color: #2c2825;
      }
      .gw-rv-average .gw-rv-average__rating .gw-rv-avg-rating .gw-rv-avg-rating__star {
        color: #2c2825;
      }
      .gw-rv-average .gw-rv-average__rating .gw-rv-average__review {
        flex-wrap: nowrap;
        font-weight: 600;
      }
      @media (max-width: 1560px) {
        .gw-rv-average .gw-rv-average__rating .gw-rv-avg-rating .gw-rv-avg-rating__star {
          width: 18px;
          height: 18px;
        }
      }
      @media (max-width: 1440px) {
        .gw-rv-average .gw-rv-average__rating .gw-rv-avg-rating .gw-rv-avg-rating__star {
          width: 16px;
          height: 16px;
        }
      }
      @media (max-width: 768px) {
        .gw-rv-average .gw-rv-average__rating .gw-rv-avg-rating {
          gap: 2px;
        }
      }
    `;

      ratingWidgetObserver.observe(ratingWidgetWrapper.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      });

      clearInterval(ratingWidgetInterval);
    }
  }, 100);
}